import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { type Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { fetchMarket } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID, TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, createIdempotentAtaIx, invokeMethod } from './common.js';

export interface ClaimBackerFeesAccounts {
  quoteMint: PublicKey;
}

export interface ClaimPrizeAccounts extends ClaimBackerFeesAccounts {
  winningOutcome: number;
  nOutcomes: number;
}

export interface ClaimPrizeParams extends BuildParams {
  claimer: PublicKey;
  market: PublicKey;
  burnAmount: BN;
  /** Indexed market facts bypass the existing public Market read. */
  claimAccounts?: ClaimPrizeAccounts;
}

export interface ClaimBackerFeesParams extends BuildParams {
  claimer: PublicKey;
  market: PublicKey;
  burnAmount: BN;
  /** Indexed market facts bypass the existing public Market read. */
  claimAccounts?: ClaimBackerFeesAccounts;
}

export async function buildClaimPrize(
  connection: Connection,
  params: ClaimPrizeParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const accounts =
    params.claimAccounts ?? (await fetchMarket(connection, params.market, programId));
  const winner = accounts.winningOutcome;
  if (!Number.isInteger(winner) || winner < 0 || winner >= accounts.nOutcomes || winner > 255) {
    throw new Error('market has no valid winning outcome');
  }
  const mint = claimPda(programId, 'claim_rights_mint', params.market, Buffer.from([winner]));
  const program = await getProgram(connection, programId);
  const instruction = await invokeMethod(program, 'claimPrize', params.burnAmount)
    .accounts({
      claimer: params.claimer,
      market: params.market,
      quoteMint: accounts.quoteMint,
      claimerQuoteAccount: getAssociatedTokenAddressSync(accounts.quoteMint, params.claimer, true),
      marketQuoteVaultAuthority: claimPda(programId, 'market_quote_vault_authority', params.market),
      marketQuoteVault: claimPda(programId, 'market_quote_vault', params.market),
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: claimPda(programId, '__event_authority'),
      program: programId,
      winningClaimRightsMint: mint,
      claimerClaimRightsAccount: getAssociatedTokenAddressSync(mint, params.claimer, true),
    })
    .instruction();
  return createBuildResult(
    [
      createIdempotentAtaIx(params.claimer, params.claimer, accounts.quoteMint, TOKEN_PROGRAM_ID),
      instruction,
    ],
    [params.claimer],
    params.computeUnits ?? estimateComputeUnits('claim_prize'),
  );
}

export async function buildClaimBackerFees(
  connection: Connection,
  params: ClaimBackerFeesParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const accounts =
    params.claimAccounts ?? (await fetchMarket(connection, params.market, programId));
  const mint = claimPda(programId, 'backer_share_mint', params.market);
  const program = await getProgram(connection, programId);
  const instruction = await invokeMethod(program, 'claimBackerFees', params.burnAmount)
    .accounts({
      claimer: params.claimer,
      market: params.market,
      quoteMint: accounts.quoteMint,
      claimerQuoteAccount: getAssociatedTokenAddressSync(accounts.quoteMint, params.claimer, true),
      marketQuoteVaultAuthority: claimPda(programId, 'market_quote_vault_authority', params.market),
      marketQuoteVault: claimPda(programId, 'market_quote_vault', params.market),
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: claimPda(programId, '__event_authority'),
      program: programId,
      backerShareMint: mint,
      claimerBackerShareAccount: getAssociatedTokenAddressSync(mint, params.claimer, true),
    })
    .instruction();
  return createBuildResult(
    [
      createIdempotentAtaIx(params.claimer, params.claimer, accounts.quoteMint, TOKEN_PROGRAM_ID),
      instruction,
    ],
    [params.claimer],
    params.computeUnits ?? estimateComputeUnits('claim_backer_fees'),
  );
}

function claimPda(
  programId: PublicKey,
  seed: string,
  market?: PublicKey,
  outcome?: Buffer,
): PublicKey {
  const seeds: Buffer[] = [Buffer.from(seed)];
  if (market) seeds.push(market.toBuffer());
  if (outcome) seeds.push(outcome);
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}
